<template>
  <v-dialog
    :fullscreen="$vuetify.breakpoint.mobile"
    :transition="dialogTransition"
    v-model="dialog"
    max-width="500px"
  >
    <template>
      <v-card class="mx-auto">
        <v-toolbar color="transparent">
          <v-toolbar-title class="identity--text text-h5">{{
            toolbarTitle
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="justClose()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-container>
          <v-row dense>
            <!-- Init Workflow / Request -->
            <v-col
              cols="12"
              :key="stage.position"
              v-for="stage in stagesOrderedByPosition"
            >
              <request-card
                v-if="isAccessRequest(type) && isInitStage(stage)"
                :editedItem="editedItem"
              ></request-card>

              <auth-view-card
                class="mt-3"
                v-if="isAuthStage(stage)"
                :editedItem="editedItem"
                :stage="stage"
                :statusOf="statusOnWorkflowFor(editedItem)"
              ></auth-view-card>

              <action-card
                class="mt-3"
                v-if="isVisibleAction(stage)"
                :type="type"
                :editedItem="editedItem"
                :stage="stage"
                :statusOf="statusOnWorkflowFor(editedItem)"
              ></action-card>

              <pass-card
                class="mt-3"
                v-if="isRequestRoleActivity(type) && isPassStage(stage)"
                :editedItem="editedItem"
                :stage="stage"
                :statusOf="statusOnWorkflowFor(editedItem)"
                :action="actionType"
              ></pass-card>

              <pre-auth-card
                class="mt-3"
                v-if="needsPreAuth(stage, editedItem)"
                :type="type"
                :editedItem="editedItem"
                :stage="stage"
                :action="actionType"
                :save="save"
                :saving="saving"
              ></pre-auth-card>

              <auth-card
                class="mt-3"
                v-if="needsAuth(stage, editedItem)"
                :type="type"
                :editedItem="editedItem"
                :stage="stage"
                :action="actionType"
                :save="save"
                :saving="saving"
              ></auth-card>

              <doc-card
                class="mt-3"
                v-if="isSomeDocRequirementActivity(type) && isInitStage(stage)"
                :editedItem="editedItem"
                :requirementId="editedItem.resource.requirementId"
                :resourceId="editedItem.resource.id"
                :avatar="managedImg(editedItem.resource.avatar)"
                :link="editedItem.resource.link"
                :hrs="editedItem.resource.hrs"
                :name="editedItem.resource.name"
                :stats="statsById(editedItem.resource.requirementId)"
                :universe="getStatsUniverse"
              ></doc-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  isAuthStage,
  isInitStage,
  isPassStage
} from '@/components/requests/workflow/stagesControl'
import {
  isAccessRequest,
  isRequestRoleActivity,
  isSomeDocRequirementActivity,
  isAuthDocRoleActivity,
  isAuthRoleActivity
} from '@/components/requests/activityControl'
import { isPhone } from '@/micro/display'
import RequestCard from '@/components/requests/views/RequestCard'
import DocCard from '@/components/requests/views/DocCard'
import AuthViewCard from '@/components/requests/views/AuthViewCard'
import ActionCard from '@/components/requests/views/ActionCard'
import PassCard from '@/components/requests/views/PassCard'
import PreAuthCard from '@/components/requests/views/PreAuthCard'
import AuthCard from '@/components/requests/views/AuthCard'
import { statusOnWorkflowFor } from '@/components/requests/status/statusControl'
import { managedImg } from '@/micro/img'
import { periodIn, resourceInRequest } from '../reqsControl'
import { isWhithinTimeToAuth } from '../auths/authsTimeControl'
import { isWhithinTimeToAuth3 } from '../auths/auths3TimeControl'

export default {
  components: {
    RequestCard,
    AuthCard,
    AuthViewCard,
    ActionCard,
    PassCard,
    PreAuthCard,
    DocCard
  },

  data: () => ({
    saving: false,
    sortAsc: true,
    actionType: '',
    dialog: false,
    editedIndex: -1,
    defaultItem: {
      id: null,
      uid: null,
      topic: '',
      created_at: null,
      party: {
        name: '',
        area: '',
        l: '',
        o: '',
        info: null
      },
      resource: {
        schedule: '',
        duration: '',
        time: '',
        schedule_start: '',
        schedule_end: '',
        site: '',
        terms: false,
        motivation: ''
      },
      stats: {
        timeQty: 0,
        areaQty: 0
      },
      workflow: {}
    },
    editedItem: {
      id: null,
      uid: null,
      topic: '',
      created_at: null,
      party: {
        name: '',
        area: '',
        l: '',
        o: '',
        info: null
      },
      resource: {
        schedule: '',
        duration: '',
        time: '',
        schedule_start: '',
        schedule_end: '',
        site: '',
        terms: false,
        motivation: ''
      },
      stats: {
        timeQty: 0,
        areaQty: 0
      },
      workflow: {}
    }
  }),

  props: {
    type: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    }
  },

  watch: {
    dialog(val) {
      val || this.close()
    }
  },

  computed: {
    ...mapGetters('sites', ['siteById']),
    ...mapGetters('workflows', ['willNextStageAuth', 'isStageCurrent']),
    ...mapGetters('requirements', ['statsById', 'getStatsUniverse']),
    ...mapGetters('workflows', [
      'willNextStageAuth1',
      'willNextStageAuth2',
      'willNextStageAuth3'
    ]),
    ...mapGetters('user', ['isSupervisor', 'isHeadOfSecurity', 'isConsultant']),

    stagesOrderedByPosition() {
      const raw = this.editedItem.workflow.stages
        ? [...this.editedItem.workflow.stages]
        : []
      return raw.sort((a, b) => a.position - b.position)
    },

    needsPreAuth() {
      return (stage, item) => {
        const canAuth =
          this.isStageCurrent(stage, item) &&
          this.isAuthRoleActivity(this.type) &&
          stage.status === 'approved'
        const resource = resourceInRequest(item)
        const onTimeAuths = isWhithinTimeToAuth(periodIn(resource))
        const isAuth1 = this.isSupervisor && this.willNextStageAuth1(item)
        const isAuth2 = this.isHeadOfSecurity && this.willNextStageAuth2(item)
        const isAuth3 = this.isConsultant && this.willNextStageAuth3(item)

        return (
          canAuth &&
          ((onTimeAuths && (isAuth1 || isAuth2)) ||
            (isAuth3 && isWhithinTimeToAuth3(periodIn(resource))))
        )
      }
    },

    needsAuth() {
      return (stage, item) =>
        this.isStageCurrent(stage, item) &&
        this.isAuthDocRoleActivity(this.type) &&
        this.willNextStageAuth(item)
    },

    isVisibleAction() {
      return stage =>
        !isAuthStage(stage) && !isInitStage(stage) && !isPassStage(stage)
    },

    toolbarTitle() {
      return this.editedItem.id === null ||
        this.editedItem.workflow.stages.length === 0
        ? ''
        : this.willNextStageAuth(this.editedItem)
        ? 'Revisión de la solicitud'
        : 'Estado de la solicitud'
    },

    currentAuths() {
      return item =>
        item.workflow.stages.filter(stage => this.isAuthStage(stage))
    },

    isPhone() {
      return isPhone(this.$vuetify)
    },

    dialogTransition() {
      return this.isPhone ? 'dialog-bottom-transition' : ''
    }
  },

  methods: {
    ...mapActions('requests', ['sendStage']),

    statusOnWorkflowFor,

    isAuthStage,

    isInitStage,

    isPassStage,

    isRequestRoleActivity,

    isAccessRequest,

    isSomeDocRequirementActivity,

    managedImg,

    isAuthDocRoleActivity,

    isAuthRoleActivity,

    open(item, status, resetTriggerBtns) {
      this.resetTriggerBtns = resetTriggerBtns
      this.actionType = status
      this.editedIndex = this.items.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },

    save(comment) {
      this.saving = true
      if (this.editedIndex > -1) {
        Object.assign(this.items[this.editedIndex], this.editedItem)
      } else {
        this.items.push(this.editedItem)
      }
      this.sendStage({
        item: this.editedItem,
        action: this.actionType,
        comment
      })
        .then(() => {
          this.close()
          this.saving = false
        })
        .catch(error => {
          this.saving = false
          this.resetTriggerBtns()
          console.log('Can not send the stage.', error)
        })
    },

    justClose() {
      this.resetTriggerBtns()
      this.close()
    },

    close() {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    isAuthDialog() {
      return this.actionType === 'to_auth'
    }
  }
}
</script>
